import React from "react";
import {
  Box,
  Heading,
  Link,
  List,
  ListItem,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";

function scrollToBlogSection(e) {
  e.preventDefault();
  const el = document.getElementById("blog-section");
  if (el) {
    el.scrollIntoView({ behavior: "smooth" });
    window.history.replaceState(null, '', '#blog-section');
  } else {
    window.location.href = "/blog#blog-section";
  }
}

export default function BestOnlineLoanOptionsCanada2026() {
  return (
    <Box maxW="1000px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 20, md: 32 }}>
      <VStack spacing={6} align="start">
        <Link
          as="button"
          onClick={scrollToBlogSection}
          aria-label="Back to Blog"
          position="fixed"
          top={{ base: "84px", md: "96px" }}
          left={{ base: 4, md: 8 }}
          zIndex="sticky"
          bg="white"
          color="teal.600"
          fontWeight="semibold"
          border="1px solid"
          borderColor="teal.100"
          borderRadius="full"
          px={{ base: 0, sm: 4 }}
          py={{ base: 0, sm: 2 }}
          w={{ base: "42px", sm: "auto" }}
          h={{ base: "42px", sm: "auto" }}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="sm"
          _hover={{ textDecoration: "none", bg: "teal.50" }}
        >
          <Text as="span" display={{ base: "inline", sm: "none" }}>←</Text>
          <Text as="span" display={{ base: "none", sm: "inline" }}>← Back to Blog</Text>
        </Link>

        <Heading as="h1" size="xl" mb={2} mt={16}>Best Online Loan Options in Canada for 2026</Heading>
        <Text fontSize="sm" color="gray.500">
          By Heavenly
          •
          March 09, 2026
          •
          5 mins read
        </Text>

        <Text>
          Looking for a loan in 2026 is less about finding the "fastest ad" and more about comparing total cost,
          repayment flexibility, and lender transparency.
        </Text>

        <Heading as="h2" size="md">Loan Options at a Glance</Heading>
        <Box overflowX={{ base: "auto", md: "visible" }} width="100%">
          <Table variant="simple" minWidth="600px">
            <Thead>
              <Tr>
                <Th>Option</Th>
                <Th>Best For</Th>
                <Th>Typical Amount</Th>
                <Th>Typical Term</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>Payday / small cash loan</Td>
                <Td>Short-term urgent expenses</Td>
                <Td>$100 - $1,500</Td>
                <Td>Next pay cycle</Td>
              </Tr>
              <Tr>
                <Td>Installment loan</Td>
                <Td>Bigger expenses with structured payments</Td>
                <Td>$1,500 - $20,000</Td>
                <Td>3 - 48 months</Td>
              </Tr>
              <Tr>
                <Td>Line of credit</Td>
                <Td>Flexible ongoing borrowing</Td>
                <Td>Varies by profile</Td>
                <Td>Revolving</Td>
              </Tr>
              <Tr>
                <Td>Secured personal loan</Td>
                <Td>Lower-rate borrowing with collateral</Td>
                <Td>Usually higher limits</Td>
                <Td>Fixed monthly term</Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>

        <Heading as="h2" size="md">What to Compare Before Applying</Heading>
        <Text>
          The same loan amount can have very different total repayment depending on structure.
        </Text>
        <Text><b>Check these six items:</b></Text>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>Total repayment amount (not just the advertised rate)</ListItem>
          <ListItem>APR and all additional fees</ListItem>
          <ListItem>Payment schedule and due-date flexibility</ListItem>
          <ListItem>Penalty rules (late, NSF, and prepayment)</ListItem>
          <ListItem>Funding speed and payout method</ListItem>
          <ListItem>Support quality if your payment fails or details change</ListItem>
        </List>

        <Heading as="h2" size="md">When Each Loan Type Makes Sense</Heading>

        <Heading as="h3" size="sm">Short-term small loan</Heading>
        <Text>
          Works best for urgent essentials where you are confident about near-term repayment capacity.
        </Text>

        <Heading as="h3" size="sm">Installment loan</Heading>
        <Text>
          Better for larger planned needs where you prefer predictable monthly or biweekly payments.
        </Text>

        <Heading as="h3" size="sm">Line of credit</Heading>
        <Text>
          Useful for variable expenses, but requires discipline because available room can encourage over-borrowing.
        </Text>

        <Heading as="h3" size="sm">Secured loan</Heading>
        <Text>
          May offer lower rates and larger limits, but includes collateral risk if payments are missed.
        </Text>

        <Heading as="h2" size="md">Red Flags to Avoid</Heading>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>Unclear fee wording such as "charges may apply" without detail</ListItem>
          <ListItem>No clear repayment schedule before acceptance</ListItem>
          <ListItem>Pressure to borrow more than you requested</ListItem>
          <ListItem>Support channels that are difficult to reach</ListItem>
          <ListItem>Any request for login credentials or card PIN</ListItem>
        </List>

        <Heading as="h2" size="md">Final Thought</Heading>
        <Text>
          The best loan option is the one that fits your repayment ability, not the one with the loudest promotion.
          Compare full cost and terms before you accept any offer.
        </Text>

        <Heading as="h2" size="md">Quick Pre-Application Checklist</Heading>
        <List spacing={3} pl={4} styleType="disc">
          <ListItem>Do I know my exact monthly affordable payment?</ListItem>
          <ListItem>Did I compare at least two offers side by side?</ListItem>
          <ListItem>Do I understand all fees and due dates?</ListItem>
          <ListItem>Do I have a plan if income is delayed?</ListItem>
        </List>
        <Box mt={10} width="100%" textAlign="center">
          <Heading as="h3" size="md" mb={3} color="teal.700">Get an instant loan now with Swift Credit</Heading>
          <Link as={RouterLink} to="/apply" onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <Box as="button" bg="teal.500" color="white" px={8} py={3} borderRadius="md" fontWeight="bold" _hover={{ bg: "teal.600" }}>
              Get a Loan
            </Box>
          </Link>
        </Box>
      </VStack>
    </Box>
  );
}